/* global globalThis */

import { isAppDemoLocation, isMarketingHostname } from '../utils/demoRouting';

const LOCAL_SESSION_KEY = 'wawiboard.local.session';
const LOCAL_DEMO_SESSION_KEY = 'wawiboard.local.demo-session';
const LOCAL_TRUSTED_DEVICE_KEY = 'wawiboard.local.trusted-device';
let activeLocalSession = null;
let isolatedDemoRoute = false;
const pendingLocalRequestControllers = new Set();

const readLocalSession = () => {
  try {
    const isDemoRoute = isolatedDemoRoute;
    const storage = isDemoRoute ? window.sessionStorage : window.localStorage;
    const storageKey = isDemoRoute ? LOCAL_DEMO_SESSION_KEY : LOCAL_SESSION_KEY;
    return JSON.parse(storage.getItem(storageKey) || 'null');
  } catch {
    return null;
  }
};

const persistLocalSession = (nextSession) => {
  if (nextSession?.demo && isolatedDemoRoute) {
    window.sessionStorage.setItem(LOCAL_DEMO_SESSION_KEY, JSON.stringify(nextSession));
    return;
  }
  window.localStorage.setItem(LOCAL_SESSION_KEY, JSON.stringify(nextSession));
};

const removeLocalSession = (currentSession) => {
  if (currentSession?.demo && isolatedDemoRoute) {
    window.sessionStorage.removeItem(LOCAL_DEMO_SESSION_KEY);
    return;
  }
  window.localStorage.removeItem(LOCAL_SESSION_KEY);
};

const withLocalAuthHeaders = (headers = {}) => {
  const nextHeaders = { ...headers };
  const hasAuthHeader = Object.keys(nextHeaders).some(key => key.toLowerCase() === 'authorization');
  const hasTrustedDeviceHeader = Object.keys(nextHeaders).some(key => key.toLowerCase() === 'x-trusted-device');
  const storedSession = activeLocalSession || readLocalSession();
  if (!hasAuthHeader && storedSession?.access_token) {
    nextHeaders.Authorization = `Bearer ${storedSession.access_token}`;
  }
  const trustedDeviceToken = storedSession?.demo || isolatedDemoRoute
    ? ''
    : window.localStorage.getItem(LOCAL_TRUSTED_DEVICE_KEY);
  if (!hasTrustedDeviceHeader && trustedDeviceToken) {
    nextHeaders['X-Trusted-Device'] = trustedDeviceToken;
  }
  return nextHeaders;
};
const localApiUnavailableError = (baseUrl) => ({
  message: `Lokale Dashboard-API ist nicht erreichbar (${baseUrl}). Starte das Dashboard mit npm start neu oder pruefe, ob npm run local:api laeuft.`
});

const LOCAL_REQUEST_TIMEOUT_MS = 30_000;

const requestLocalJson = async (baseUrl, path, options = {}) => {
  const timeoutMs = Number.isFinite(Number(options.timeoutMs))
    ? Math.max(1_000, Number(options.timeoutMs))
    : LOCAL_REQUEST_TIMEOUT_MS;
  const requestOptions = { ...options };
  delete requestOptions.timeoutMs;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  if (controller) pendingLocalRequestControllers.add(controller);
  const timeoutHandle = controller
    ? globalThis.setTimeout(() => controller.abort(), timeoutMs)
    : null;

  try {
    const response = await fetch(`${baseUrl}${path}`, {
      ...requestOptions,
      ...(controller ? { signal: controller.signal } : {}),
      credentials: 'include',
      headers: withLocalAuthHeaders(requestOptions.headers || {})
    });
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        payload,
        error: {
          message: payload.error || payload.message || response.statusText || 'Lokale Anfrage fehlgeschlagen.',
          status: response.status
        }
      };
    }

    return { payload, error: null };
  } catch (error) {
    if (error?.name === 'AbortError') {
      return {
        payload: null,
        error: {
          message: `Lokale Anfrage hat nach ${Math.round(timeoutMs / 1000)} Sekunden abgebrochen.`,
          code: 'LOCAL_REQUEST_TIMEOUT'
        }
      };
    }
    return { payload: null, error: localApiUnavailableError(baseUrl) };
  } finally {
    if (timeoutHandle) globalThis.clearTimeout(timeoutHandle);
    if (controller) pendingLocalRequestControllers.delete(controller);
  }
};

class LocalQueryBuilder {
  constructor(baseUrl, table) {
    this.baseUrl = baseUrl;
    this.table = table;
    this.action = 'select';
    this.selectValue = '*';
    this.filters = [];
    this.orderValue = null;
    this.limitValue = null;
    this.rangeValue = null;
    this.bodyValue = null;
    this.upsertValue = false;
    this.onConflictValue = '';
    this.singleMode = '';
  }

  select(value = '*') {
    this.selectValue = value;
    return this;
  }

  eq(column, value) { this.filters.push({ op: 'eq', column, value }); return this; }
  neq(column, value) { this.filters.push({ op: 'neq', column, value }); return this; }
  gte(column, value) { this.filters.push({ op: 'gte', column, value }); return this; }
  lte(column, value) { this.filters.push({ op: 'lte', column, value }); return this; }
  gt(column, value) { this.filters.push({ op: 'gt', column, value }); return this; }
  lt(column, value) { this.filters.push({ op: 'lt', column, value }); return this; }
  in(column, value) { this.filters.push({ op: 'in', column, value }); return this; }
  is(column, value) { this.filters.push({ op: 'is', column, value }); return this; }
  not(column, operator, value) { this.filters.push({ op: 'not', column, operator, value }); return this; }

  order(column, options = {}) {
    this.orderValue = { column, ...options };
    return this;
  }

  limit(value) {
    this.limitValue = value;
    return this;
  }

  range(from, to) {
    this.rangeValue = { from, to };
    return this;
  }

  maybeSingle() {
    this.singleMode = 'maybe';
    this.limitValue = this.limitValue || 1;
    return this;
  }

  single() {
    this.singleMode = 'single';
    this.limitValue = this.limitValue || 1;
    return this;
  }

  insert(value) {
    this.action = 'insert';
    this.bodyValue = value;
    return this;
  }

  upsert(value, options = {}) {
    this.action = 'insert';
    this.bodyValue = value;
    this.upsertValue = true;
    this.onConflictValue = options.onConflict || '';
    return this;
  }

  update(value) {
    this.action = 'update';
    this.bodyValue = value;
    return this;
  }

  delete() {
    this.action = 'delete';
    return this;
  }

  async execute() {
    const { payload, error } = await requestLocalJson(this.baseUrl, '/api/local-db/query', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: this.action,
        table: this.table,
        select: this.selectValue,
        filters: this.filters,
        order: this.orderValue,
        limit: this.limitValue,
        range: this.rangeValue,
        values: this.bodyValue,
        upsert: this.upsertValue,
        onConflict: this.onConflictValue
      })
    });

    if (error) return { data: null, error };

    let data = payload.data || [];
    if (this.singleMode === 'maybe') data = data[0] || null;
    if (this.singleMode === 'single') {
      if (!data[0]) return { data: null, error: { message: 'No rows returned' } };
      data = data[0];
    }
    return { data, error: null };
  }

  then(resolve, reject) {
    return this.execute().then(resolve, reject);
  }
}

class LocalRpcBuilder {
  constructor(baseUrl, name, params = {}) {
    this.baseUrl = baseUrl;
    this.name = name;
    this.params = params;
    this.rangeValue = null;
  }

  range(from, to) {
    this.rangeValue = { from, to };
    return this;
  }

  async execute() {
    const { payload, error } = await requestLocalJson(this.baseUrl, '/api/local-db/rpc', {
      method: 'POST',
      // Large date ranges may need longer while other dashboard queries run.
      timeoutMs: this.name === 'dashboard_shipping_cost_by_article_group' ? 90_000 : LOCAL_REQUEST_TIMEOUT_MS,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: this.name,
        params: this.params || {},
        range: this.rangeValue
      })
    });

    if (error) return { data: null, error };
    return { data: payload.data, error: null };
  }

  then(resolve, reject) {
    return this.execute().then(resolve, reject);
  }
}

export const createLocalDatabaseClient = (baseUrl, options = {}) => {
  const currentLocation = options.location ?? window.location;
  isolatedDemoRoute = options.demoRoute ?? isAppDemoLocation(currentLocation);
  let session = null;
  try {
    session = readLocalSession();
  } catch {
    session = null;
  }
  if (session?.demo && isMarketingHostname(currentLocation?.hostname)) {
    window.localStorage.removeItem(LOCAL_SESSION_KEY);
    session = null;
  }
  activeLocalSession = session;

  const notify = (event, nextSession) => {
    window.dispatchEvent(new CustomEvent('wawiboard-local-auth', { detail: { event, session: nextSession } }));
  };

  return {
    cancelPendingRequests() {
      pendingLocalRequestControllers.forEach(controller => controller.abort());
      pendingLocalRequestControllers.clear();
    },
    from(table) {

      return new LocalQueryBuilder(baseUrl, table);
    },
    rpc(name, params) {
      return new LocalRpcBuilder(baseUrl, name, params);
    },
    tenants: {
      async saveLogo(tenantId, logoDataUrl) {
        const { payload, error } = await requestLocalJson(baseUrl, `/api/tenants/${encodeURIComponent(tenantId)}/logo`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'X-Tenant-Id': tenantId } : {})
          },
          body: JSON.stringify({ logoDataUrl })
        });
        return error ? { data: null, error } : { data: payload?.tenant || null, error: null };
      },
      async deleteLogo(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, `/api/tenants/${encodeURIComponent(tenantId)}/logo`, {
          method: 'DELETE',
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload?.tenant || null, error: null };
      }
    },
    billing: {
      async get(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/billing', {
          headers: { 'X-Tenant-Id': tenantId }
        });
        return error ? { data: null, error } : { data: payload, error: null };
      },
      async checkout(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/billing/checkout', {
          method: 'POST', headers: { 'X-Tenant-Id': tenantId }
        });
        return error ? { data: null, error } : { data: payload, error: null };
      },
      async portal(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/billing/portal', {
          method: 'POST', headers: { 'X-Tenant-Id': tenantId }
        });
        return error ? { data: null, error } : { data: payload, error: null };
      }
    },
    integrations: {
      async getAmazonAnalytics(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/amazon/analytics', {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async syncAmazonAnalytics(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/amazon/analytics/sync', {
          method: 'POST',
          timeoutMs: 300_000,
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async beginAuthorization(tenantId, provider) {
        const { payload, error } = await requestLocalJson(baseUrl, `/api/integrations/${encodeURIComponent(provider)}/authorize`, {
          method: 'POST',
          headers: { ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}) }
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async list(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations', {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload?.connections || [], error: null };
      },
      async sync(tenantId, provider) {
        const { payload, error } = await requestLocalJson(baseUrl, `/api/integrations/${encodeURIComponent(provider)}/sync`, {
          method: 'POST',
          headers: { ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}) }
        });
        return error ? { data: null, error } : { data: payload?.connection || null, error: null };
      },
      async testDhl(tenantId, apiKey, apiSecret) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/test', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'X-Tenant-Id': tenantId } : {})
          },
          body: JSON.stringify({ apiKey, apiSecret })
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async testSavedDhl(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/test-saved', {
          method: 'POST',
          headers: { ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}) }
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async googleAnalyticsProperties(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/google_analytics/properties', {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async selectGoogleAnalyticsProperty(tenantId, propertyId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/google_analytics/property', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'X-Tenant-Id': tenantId } : {})
          },
          body: JSON.stringify({ propertyId })
        });
        return error ? { data: null, error } : { data: payload?.property || null, error: null };
      },
      async saveCredentials(tenantId, provider, credentials) {
        const { payload, error } = await requestLocalJson(baseUrl, `/api/integrations/${encodeURIComponent(provider)}/credentials`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'X-Tenant-Id': tenantId } : {})
          },
          body: JSON.stringify(credentials || {})
        });
        return error ? { data: null, error } : { data: payload?.connection || null, error: null };
      },
      async importDhlBilling(tenantId, file) {
        const csvText = await file.text();
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/billing-import', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'X-Tenant-Id': tenantId } : {})
          },
          body: JSON.stringify({ filename: file.name, csvText })
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async uploadDhlBillingDocument(tenantId, file) {
        try {
          const extension = String(file.name || '').split('.').pop().toLowerCase();
          const contentType = {
            pdf: 'application/pdf',
            csv: 'text/csv',
            xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          }[extension] || 'application/octet-stream';
          const response = await fetch(`${baseUrl}/api/integrations/dhl/billing-documents`, {
            method: 'POST',
            credentials: 'include',
            headers: withLocalAuthHeaders({
              'Content-Type': contentType,
              'X-File-Name': encodeURIComponent(file.name || 'dhl-rechnung.pdf'),
              ...(tenantId ? { 'X-Tenant-Id': tenantId } : {})
            }),
            body: file
          });
          const payload = await response.json().catch(() => ({}));
          return response.ok
            ? { data: payload.document ? { ...payload.document, ...(payload.import ? { import: payload.import } : {}), ...(payload.summary ? { summary: payload.summary } : {}) } : null, error: null }
            : { data: null, error: { message: payload.error || response.statusText || 'DHL-Datei konnte nicht hochgeladen werden.', status: response.status } };
        } catch {
          return { data: null, error: localApiUnavailableError(baseUrl) };
        }
      },
      async listDhlBillingDocuments(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/billing-documents', {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload?.documents || [], error: null };
      },
      async listDhlTariffs(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/tariffs', {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload || { tiers: [], methods: [] }, error: null };
      },
      async getDhlBillingPrices(tenantId, startDate, endDate) {
        const query = new URLSearchParams({ startDate: String(startDate || ''), endDate: String(endDate || '') });
        const { payload, error } = await requestLocalJson(baseUrl, `/api/integrations/dhl/billing-prices?${query}`, {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async getDhlBillingUnpricedShipments(tenantId, startDate, endDate, shippingMethod, page = 1) {
        const query = new URLSearchParams({
          startDate: String(startDate || ''), endDate: String(endDate || ''),
          detailMethod: String(shippingMethod || ''), page: String(page)
        });
        const { payload, error } = await requestLocalJson(baseUrl, `/api/integrations/dhl/billing-prices?${query}`, {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async getDhlReconciliation(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/reconciliation', {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async listDhlTrackingShipments(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/tracking', {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload?.shipments || [], error: null };
      },
      async saveDhlTariff(tenantId, tier) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/tariffs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}) },
          body: JSON.stringify(tier)
        });
        return error ? { data: null, error } : { data: payload?.tier || null, error: null };
      },
      async copyDhlTariffs(tenantId, sourceMethod, targetMethod) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/integrations/dhl/tariffs/copy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}) },
          body: JSON.stringify({ sourceMethod, targetMethod })
        });
        return error ? { data: null, error } : { data: payload, error: null };
      },
      async deleteDhlTariff(tenantId, tier) {
        const query = new URLSearchParams({
          shippingMethod: tier.shipping_method,
          validFrom: String(tier.valid_from).slice(0, 10),
          maxWeightKg: String(tier.max_weight_kg)
        });
        const { payload, error } = await requestLocalJson(baseUrl, `/api/integrations/dhl/tariffs?${query}`, {
          method: 'DELETE',
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload || null, error: null };
      },
      async openDhlBillingDocument(tenantId, documentId) {
        try {
          const response = await fetch(`${baseUrl}/api/integrations/dhl/billing-documents/${encodeURIComponent(documentId)}`, {
            credentials: 'include',
            headers: withLocalAuthHeaders(tenantId ? { 'X-Tenant-Id': tenantId } : {})
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            return { data: null, error: { message: payload.error || response.statusText || 'DHL-Rechnung konnte nicht geöffnet werden.', status: response.status } };
          }
          return { data: await response.blob(), error: null };
        } catch {
          return { data: null, error: localApiUnavailableError(baseUrl) };
        }
      },
      async disconnect(tenantId, provider) {
        const { payload, error } = await requestLocalJson(baseUrl, `/api/integrations/${encodeURIComponent(provider)}`, {
          method: 'DELETE',
          headers: { ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}) }
        });
        return error ? { data: null, error } : { data: payload?.connection || null, error: null };
      }
    },
    notifications: {
      async getState(tenantId) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/notifications/state', {
          headers: tenantId ? { 'X-Tenant-Id': tenantId } : {}
        });
        return error ? { data: null, error } : { data: payload, error: null };
      },
      async saveState(tenantId, state) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/notifications/state', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(tenantId ? { 'X-Tenant-Id': tenantId } : {})
          },
          body: JSON.stringify({ tenantId, state })
        });
        return error ? { data: null, error } : { data: payload, error: null };
      }
    },
    auth: {
      async signInWithPassword(credentials) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/sign-in', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(credentials || {})
        });

        if (error) return { data: null, error };
        if (payload?.mfa?.challengeToken) {
          window.localStorage.removeItem(LOCAL_TRUSTED_DEVICE_KEY);
          return {
            data: {
              session: null,
              user: null,
              mfa: payload.mfa
            },
            error: null
          };
        }
        if (!payload?.session?.user) {
          return { data: null, error: { message: 'Lokale Anmeldung hat keine gueltige Session geliefert.' } };
        }

        session = payload.session;
        activeLocalSession = session;
        persistLocalSession(session);
        notify('SIGNED_IN', session);
        return { data: { session, user: session.user }, error: null };
      },
      async requestPasswordReset(email) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/password-reset/request', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        return error ? { data: null, error } : { data: payload, error: null };
      },
      async confirmPasswordReset({ challengeToken, code, password } = {}) {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/password-reset/confirm', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ challengeToken, code, password })
        });
        if (error) return { data: null, error };
        const previousSession = session;
        session = null;
        activeLocalSession = null;
        removeLocalSession(previousSession);
        window.localStorage.removeItem(LOCAL_TRUSTED_DEVICE_KEY);
        notify('SIGNED_OUT', null);
        return { data: payload, error: null };
      },
      async signInWithDemo() {
        const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/demo', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (error) return { data: null, error };
        if (!payload?.session?.user) {
          return { data: null, error: { message: 'Demo-Session konnte nicht gestartet werden.' } };
        }

        session = { ...payload.session, tenant_id: payload.tenantId, demo: true };
        activeLocalSession = session;
        persistLocalSession(session);
        notify('SIGNED_IN', session);
        return { data: { session, user: session.user, tenantId: payload.tenantId }, error: null };
      },
      async signOut() {
        await requestLocalJson(baseUrl, '/api/local-auth/sign-out', { method: 'POST' });
        const previousSession = session;
        session = null;
        activeLocalSession = null;
        removeLocalSession(previousSession);
        notify('SIGNED_OUT', null);
        return { error: null };
      },
      async getSession() {
        if (!session?.access_token) return { data: { session: null }, error: null };
        const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/session');
        if (error && ![401, 403].includes(Number(error.status))) {
          return { data: { session }, error };
        }
        if (error || !payload?.session?.user) {
          const previousSession = session;
          session = null;
          activeLocalSession = null;
          removeLocalSession(previousSession);
          notify('SIGNED_OUT', null);
          return { data: { session: null }, error: null };
        }
        session = payload.session;
        activeLocalSession = session;
        persistLocalSession(session);
        return { data: { session }, error: null };
      },
      onAuthStateChange(callback) {
        const handler = (event) => callback(event.detail?.event || 'TOKEN_REFRESHED', event.detail?.session || null);
        window.addEventListener('wawiboard-local-auth', handler);
        return {
          data: {
            subscription: {
              unsubscribe: () => window.removeEventListener('wawiboard-local-auth', handler)
            }
          }
        };
      },
      mfa: {
        async verify({ challengeToken, code, trustDevice = true } = {}) {
          const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/email/verify', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ challengeToken, code, trustDevice })
          });
          if (error) return { data: null, error };
          if (!payload?.session?.user) {
            return { data: null, error: { message: 'E-Mail-Bestätigung hat keine gültige Session geliefert.' } };
          }
          if (payload.trustedDeviceToken) {
            window.localStorage.setItem(LOCAL_TRUSTED_DEVICE_KEY, payload.trustedDeviceToken);
          } else if (payload.forgetTrustedDevice) {
            window.localStorage.removeItem(LOCAL_TRUSTED_DEVICE_KEY);
          }
          session = payload.session;
          activeLocalSession = session;
          persistLocalSession(session);
          notify('SIGNED_IN', session);
          return { data: { session, user: session.user }, error: null };
        }
      },
      async updateUser(updates = {}) {
        if (!session?.user) return { data: { user: null }, error: { message: 'Keine lokale Session aktiv.' } };
        if (updates.password) {
          const { payload, error } = await requestLocalJson(baseUrl, '/api/local-auth/password/change', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              currentPassword: updates.currentPassword,
              password: updates.password
            })
          });
          if (error) return { data: { user: null }, error };
          const previousSession = session;
          session = null;
          activeLocalSession = null;
          removeLocalSession(previousSession);
          window.localStorage.removeItem(LOCAL_TRUSTED_DEVICE_KEY);
          notify('SIGNED_OUT', null);
          return { data: { user: null, ...payload }, error: null };
        }
        const nextUser = {
          ...session.user,
          email: updates.email || session.user.email,
          user_metadata: {
            ...(session.user.user_metadata || {}),
            ...(updates.data || {})
          }
        };
        session = { ...session, user: nextUser };
        activeLocalSession = session;
        persistLocalSession(session);
        notify('USER_UPDATED', session);
        return { data: { user: nextUser }, error: null };
      }
    }
  };
};








